import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import { object, string, date, number } from "yup";
import Regex from "../../utilities/constants/Regex";
import { yupResolver } from "@hookform/resolvers/yup";
import { createAppointment } from "./AppointmentServices";
import { useUser } from "../user/UserProvider";
import {
  formatDateForInput,
  isDateInTheFuture,
  isTimeBetweenBoundaries,
  timeOptions,
} from "../../utilities/functions/Helpers";
import { ToastContainer } from "react-toastify";
import FacilityConstants from "../../utilities/constants/FacilityConstants";
import { useLoadingSpinner } from "../../utilities/components/LoadingSpinnerProvider";

const schema = object().shape({
  id: number().nullable().optional(),
  firstName: string().required("First name is required"),
  lastName: string().required("Last name is required"),
  email: string().email().required("Email is required"),
  phone: string()
    .required("Phone is required")
    .matches(Regex.PHONE_REGEX, "Invalid phone number"),
  dateTime: date()
    .transform((value, originalValue) => {
      return originalValue ? new Date(originalValue) : null;
    })
    .test("is-between", "Time must be between 9:00 AM and 7:30 PM", (value) =>
      isTimeBetweenBoundaries(value),
    )
    .test("is-in-the-future", "Date and time must be in the future", (value) =>
      isDateInTheFuture(value),
    )
    .required("Date and time are required"),
  raison: string().required("The raison is required"),
  status: string().nullable().optional(),
  additionalInfo: string().nullable().optional(),
  appointmentType: string().required("Appointment type is required"),
  reminderPreferences: string().optional(),
  userId: string().nullable().optional(),
});

/**
 * The form to create or update an appointment.
 * @param {object} props  The props passed to the component when updating an appointment.
 * @returns  The form to create or update an appointment.
 */
const AppointmentForm = (props) => {
  const {
    appointment,
    updateAppointment,
    toggleModal,
    setState,
    isUpdating = false,
    onSuccess,
  } = props;
  const [apiError, setApiError] = useState(false);
  const [resetted, setResetted] = useState(false);
  const { userProfile } = useUser();
  const setIsLoading = useLoadingSpinner();
  const {
    clearErrors,
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      id: appointment?.id ?? null,
      firstName: appointment?.firstName ?? userProfile?.firstName ?? "",
      lastName: appointment?.lastName ?? userProfile?.lastName ?? "",
      email: appointment?.email ?? userProfile?.email ?? "",
      phone: appointment?.phone ?? userProfile?.phone ?? "",
      dateTime: appointment?.dateTime
        ? formatDateForInput(appointment?.dateTime)
        : "",
      raison: appointment?.raison ?? "",
      status: appointment?.status ?? FacilityConstants.STATUS.PENDING,
      additionalInfo: appointment?.additionalInfo ?? "",
      appointmentType: appointment?.appointmentType ?? "",
      reminderPreferences: appointment?.reminderPreferences ?? "EMAIL",
      userId: appointment?.userId ?? userProfile?.userId ?? "",
    },
  });

  const now = new Date().toLocaleString("en-US", timeOptions);
  const todayAt12AM = new Date(now).setHours(0, 0, 0, 0);
  const nextDayAt12AM = new Date(todayAt12AM).setHours(20, 0, 0, 0);

  const minDateTime =
    new Date(now) > new Date(now).setHours(19, 30, 0, 0)
      ? new Date(nextDayAt12AM).toISOString().slice(0, 16)
      : new Date(todayAt12AM).toISOString().slice(0, 16);

  const setFalsyValuesToNull = (data) => {
    Object.keys(data).forEach((key) => {
      if (!data[key]) {
        data[key] = null;
      }
    });
    return data;
  };

  const onSubmit = async (data) => {
    data.dateTime = new Date(data.dateTime).toISOString();
    data.status = FacilityConstants.STATUS.BOOKED;
    data = setFalsyValuesToNull(data);

    setIsLoading(true);
    try {
      let response;
      if (isUpdating) {
        if (
          (!!data.userId && userProfile.userId !== data.userId) ||
          (!data.userId && userProfile.userId)
        ) {
          data.userId = userProfile.userId;
        }
        response = await updateAppointment(data, setApiError, setState, setIsLoading);
      } else {
        data.userId = !!userProfile.userId ? userProfile.userId : null;
        response = await createAppointment(data, setApiError, setState, setIsLoading);
      }

      reset();
      onSuccess?.(response ?? data, isUpdating ? "updated" : "created");
      toggleModal !== undefined && toggleModal(false);
    } catch {
      // Error feedback is handled in the service layer.
    }
  };

  const handleCancel = () => {
    clearErrors();
    reset({
      id: null,
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dateTime: "",
      raison: "",
      status: appointment?.status ?? FacilityConstants.STATUS.PENDING,
      additionalInfo: appointment?.additionalInfo ?? "",
      appointmentType: appointment?.appointmentType ?? "",
      reminderPreferences: appointment?.reminderPreferences ?? "EMAIL",
      userId: appointment?.userId ?? userProfile?.userId ?? "",
    });
    setResetted(true);
    toggleModal !== undefined && toggleModal(false);
  };

  useEffect(() => {
    if (userProfile && !isUpdating && !resetted) {
      setValue("firstName", userProfile.firstName);
      setValue("lastName", userProfile.lastName);
      setValue("email", userProfile.email);
      setValue("phone", userProfile?.phone ?? "");
      setValue("appointmentType", "Consultation"); // Default for new appointments
    }
  }, [userProfile, setValue, isUpdating, resetted]);

  return (
    <div className="w-full">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">
        {/* Banner Section */}
        <div className="h-32 bg-gradient-to-r from-purple-900 via-fuchsia-900 to-slate-900 relative">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
          <div className="absolute bottom-4 left-6 md:left-8 text-white">
            <h1 className="text-2xl md:text-3xl font-serif font-bold text-white drop-shadow-md">
              {isUpdating ? "Edit Appointment" : "New Appointment"}
            </h1>
            <p className="text-white/80 text-sm mt-1 font-medium">
              Schedule a consultation or viewing.
            </p>
          </div>
        </div>

        <div className="px-6 py-8 md:px-8">
          <form className="w-full space-y-8" onSubmit={handleSubmit(onSubmit)}>
            {/* Personal Information */}
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                <span className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </span>
                Contact Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* First Name */}
                <div className="space-y-2">
                  <label
                    htmlFor="firstName"
                    className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
                  >
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    placeholder="First name"
                    className={`w-full px-4 py-3 rounded-xl border ${errors.firstName ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none`}
                    {...register("firstName")}
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-xs mt-1 ml-1">
                      {errors.firstName.message}
                    </p>
                  )}
                </div>

                {/* Last Name */}
                <div className="space-y-2">
                  <label
                    htmlFor="lastName"
                    className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
                  >
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    placeholder="Last name"
                    className={`w-full px-4 py-3 rounded-xl border ${errors.lastName ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none`}
                    {...register("lastName")}
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-xs mt-1 ml-1">
                      {errors.lastName.message}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
                  >
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    placeholder="example@domain.com"
                    className={`w-full px-4 py-3 rounded-xl border ${errors.email ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none`}
                    {...register("email")}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1 ml-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <label
                    htmlFor="phone"
                    className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
                  >
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="1 (555) 555-5555"
                    className={`w-full px-4 py-3 rounded-xl border ${errors.phone ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none`}
                    {...register("phone")}
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-xs mt-1 ml-1">
                      {errors.phone.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Appointment Details */}
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                <span className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-lg text-purple-600 dark:text-purple-400">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
                    <line x1="16" x2="16" y1="2" y2="6" />
                    <line x1="8" x2="8" y1="2" y2="6" />
                    <line x1="3" x2="21" y1="10" y2="10" />
                  </svg>
                </span>
                Appointment Details
              </h3>

              <div className="space-y-6">
                {/* Date and Time */}
                <div className="space-y-2">
                  <label
                    htmlFor="dateTime"
                    className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
                  >
                    Date and Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    min={minDateTime}
                    className={`w-full px-4 py-3 rounded-xl border ${errors.dateTime ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none dark:calendar-invert`}
                    {...register("dateTime")}
                  />
                  {errors.dateTime && (
                    <p className="text-red-500 text-xs mt-1 ml-1">
                      {errors.dateTime.message}
                    </p>
                  )}
                </div>

                {/* Reason */}
                <div className="space-y-2">
                  <label
                    htmlFor="raison"
                    className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
                  >
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <input
                    placeholder="e.g. Venue Tour, Event Planning Consultation"
                    className={`w-full px-4 py-3 rounded-xl border ${errors.raison ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none`}
                    {...register("raison")}
                  />
                  {errors.raison && (
                    <p className="text-red-500 text-xs mt-1 ml-1">
                      {errors.raison.message}
                    </p>
                  )}
                </div>

                {/* Additional Info */}
                <div className="space-y-2">
                  <label
                    htmlFor="additionalInfo"
                    className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
                  >
                    Additional Info{" "}
                    <span className="text-slate-400 text-xs font-normal">
                      (Optional)
                    </span>
                  </label>
                  <textarea
                    name="additionalInfo"
                    placeholder="Any specific questions or requirements?"
                    maxLength={2000}
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none resize-none"
                    {...register("additionalInfo")}
                  />
                </div>

                {/* Appointment Type */}
                <div className="space-y-2">
                  <label
                    htmlFor="appointmentType"
                    className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
                  >
                    Appointment Type <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="appointmentType"
                      className={`w-full px-4 py-3 rounded-xl border ${errors.appointmentType ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none appearance-none`}
                      {...register("appointmentType")}
                    >
                      <option value="">Select type...</option>
                      <option value="Consultation">Consultation</option>
                      <option value="Viewing">Viewing</option>
                      <option value="Event Planning">Event Planning</option>
                      <option value="Follow-up">Follow-up</option>
                      <option value="Other">Other</option>
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                  {errors.appointmentType && (
                    <p className="text-red-500 text-xs mt-1 ml-1">
                      {errors.appointmentType.message}
                    </p>
                  )}
                </div>

                {/* Reminder Preferences */}
                <div className="space-y-2">
                  <label
                    htmlFor="reminderPreferences"
                    className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
                  >
                    Reminder Preferences
                  </label>
                  <div className="relative">
                    <select
                      id="reminderPreferences"
                      className={`w-full px-4 py-3 rounded-xl border ${errors.reminderPreferences ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none appearance-none`}
                      {...register("reminderPreferences")}
                    >
                      <option value="EMAIL">Email</option>
                      <option value="SMS">SMS</option>
                      <option value="EMAIL,SMS">Email & SMS</option>
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-row justify-end gap-4 pt-6 mt-8 border-t border-slate-200 dark:border-slate-800">
              <button
                className="px-6 py-3 rounded-xl font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                type="button"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                className="px-8 py-3 rounded-xl font-bold bg-blue-600 dark:bg-blue-600 text-white hover:bg-blue-700 dark:hover:bg-blue-500 shadow-lg shadow-blue-500/30 transition-all hover:-translate-y-0.5"
                type="submit"
              >
                {isUpdating ? "Update Appointment" : "Submit Request"}
              </button>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer position="bottom-right" theme="colored" />
    </div>
  );
};

AppointmentForm.propTypes = {
  appointment: PropTypes.object,
  updateAppointment: PropTypes.func,
  toggleModal: PropTypes.func,
  setState: PropTypes.func,
  isUpdating: PropTypes.bool,
  onSuccess: PropTypes.func,
};

export default AppointmentForm;
